import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { WeeklyPlan as WeeklyPlanType, GoalFeasibility, RaceGoal } from '../../types';
import Navbar from '../Layout/Navbar';
import AdherenceDashboard from './AdherenceDashboard';
import AvailabilityModal from './AvailabilityModal';
import { useUnits } from '../../contexts/UnitsContext';
import './WeeklyPlan.css';

interface WorkoutStep {
  label: string;
  type: 'warmup' | 'main' | 'surge' | 'cooldown' | 'rest';
  why: string;
  distance_miles?: number;
  duration_min?: number;
  pace_label?: string;
  hr_label?: string;
}

interface WorkoutStructure {
  purpose: string;
  steps: WorkoutStep[];
}

interface PlannedWorkout {
  id: number;
  planned_date: string;
  day: string;
  workout_type: string;
  planned_distance: number;
  planned_pace: number;
  planned_notes: string;
  workout_structure?: WorkoutStructure;
  status: string;
  adherence_score: number;
  adherence_explanation: string;
  completed_workout: {
    id: number;
    distance: number;
    duration_seconds: number;
    avg_pace: number;
    date: string;
  } | null;
}

interface WeeklyAdherence {
  adherence_score: number;
  workouts_completed: number;
  workouts_planned: number;
  mileage_completed: number;
  mileage_planned: number;
  long_run_completed: boolean;
  quality_workout_completed: boolean;
}

const WeeklyPlan: React.FC = () => {
  const navigate = useNavigate();
  const [plan, setPlan] = useState<WeeklyPlanType | null>(null);
  const [feasibility, setFeasibility] = useState<GoalFeasibility | null>(null);
  const [plannedWorkouts, setPlannedWorkouts] = useState<PlannedWorkout[]>([]);
  const [adherence, setAdherence] = useState<WeeklyAdherence | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [planHistory, setPlanHistory] = useState<any[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [movingWorkoutId, setMovingWorkoutId] = useState<number | null>(null);
  const [moveError, setMoveError] = useState<string | null>(null);
  const [planStatus, setPlanStatus] = useState<string>('active');
  const [showPlanMenu, setShowPlanMenu] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [selectedWeekStart, setSelectedWeekStart] = useState<string | null>(null);
  const [isEmptyWeek, setIsEmptyWeek] = useState(false);
  const [planInactive, setPlanInactive] = useState<{ status: string; race_name: string; race_date: string } | null>(null);
  const [extraWorkouts, setExtraWorkouts] = useState<Record<string, any[]>>({});
  const [crossTraining, setCrossTraining] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'schedule' | 'overview' | 'adherence'>('schedule');
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());
  const [raceGoal, setRaceGoal] = useState<RaceGoal | null>(null);

  // Viewport width — drives how many mileage bars fit without overflowing the card
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Availability modal
  const [showAvailModal, setShowAvailModal] = useState(false);
  const [pendingRegenerate, setPendingRegenerate] = useState(false);
  const [defaultRunDays, setDefaultRunDays] = useState<string[]>([]);
  const [defaultStrengthDays, setDefaultStrengthDays] = useState<string[]>([]);
  const [defaultCyclingDays, setDefaultCyclingDays] = useState<string[]>([]);
  const [defaultWalkingDays, setDefaultWalkingDays] = useState<string[]>([]);
  const [defaultRestDays, setDefaultRestDays] = useState<string[]>([]);

  const errorRef = useRef<HTMLDivElement>(null);
  const isInitialMount = useRef(true);

  useEffect(() => {
    loadPlanAndFeasibility(null);
    loadPlanHistory();
    loadPlanStatus();
    loadProfileDays();
    api.trackEvent('plan_viewed');
  }, []);

  // Scroll to error message when it appears
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);

  // Check for auto-prompt whenever plan or planHistory changes
  useEffect(() => {
    if (plan) {
      checkAutoPrompt();
    }
  }, [plan, planHistory]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reload data when selected week changes (including back to current week)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    loadPlanAndFeasibility(selectedWeekStart);
  }, [selectedWeekStart]); // eslint-disable-line react-hooks/exhaustive-deps

  // The AI coach reviews and corrects the week in the background right after
  // generation. Until that finishes the page shows a spinner, never the raw
  // plan. Poll until review_pending clears, then load the finished week.
  useEffect(() => {
    if (!plan?.review_pending) return;
    const t = setInterval(() => {
      api.getCurrentPlan(plan.week_start_date).then(p => {
        if (!p.review_pending) loadPlanAndFeasibility(plan.week_start_date);
      }).catch(() => {});
    }, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan?.review_pending, plan?.week_start_date]);

  const loadPlanAndFeasibility = async (weekStart: string | null) => {
    try {
      setLoading(true);
      setIsEmptyWeek(false);
      setAdherence(null); // Always reset so future weeks don't show stale data

      // Try to load existing plan (for selected week or current week)
      try {
        const planData = await api.getCurrentPlan(weekStart || undefined);

        // Backend signals "no active plan" with plan_active: false
        const pd = planData as any;
        if (pd && pd.plan_active === false) {
          setPlanInactive({
            status: pd.last_plan_status,
            race_name: pd.last_race_name,
            race_date: pd.last_race_date,
          });
          setPlan(null);
          setPlannedWorkouts([]);
          setExtraWorkouts({});
          return;
        }

        setPlanInactive(null);
        setPlan(planData);

        // Use the plan's actual week_start_date so getPlannedWorkouts fetches the same week.
        // When no weekStart is specified, getCurrentPlan may fall back to a past plan whose
        // week differs from "today's week" — using the plan's date keeps them in sync.
        const effectiveWeekStart = weekStart || planData.week_start_date;

        // Load planned workouts with completion status
        const plannedData = await api.getPlannedWorkouts(effectiveWeekStart);
        setPlannedWorkouts(plannedData.workouts);
        setExtraWorkouts(plannedData.extra_workouts || {});
        setCrossTraining(plannedData.cross_training || []);

        // Load weekly adherence — may return null/error for future weeks
        try {
          const adherenceData = await api.getWeeklyAdherence(effectiveWeekStart);
          // Only apply if the week has started (mileage_planned > 0 or workouts_planned > 0)
          if (adherenceData && (adherenceData.workouts_planned > 0 || adherenceData.mileage_completed > 0)) {
            setAdherence(adherenceData);
          }
        } catch (err: any) {
          console.log('No adherence data yet');
        }
      } catch (err: any) {
        if (weekStart) {
          // User explicitly navigated to this week but no plan exists — show empty week state
          setIsEmptyWeek(true);
          setPlan(null);
          setPlannedWorkouts([]);
          // Still fetch any actual workouts logged this week (unplanned runs)
          try {
            const plannedData = await api.getPlannedWorkouts(weekStart);
            setExtraWorkouts(plannedData.extra_workouts || {});
            setCrossTraining(plannedData.cross_training || []);
          } catch {
            setExtraWorkouts({});
            setCrossTraining([]);
          }
        } else {
          // No plan at all yet
          console.log('No plan exists yet');
        }
      }

      // Load goal feasibility
      try {
        const feasData = await api.getGoalFeasibility();
        setFeasibility(feasData);
      } catch (err: any) {
        console.log('Could not load feasibility');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadPlanHistory = async () => {
    try {
      const historyData = await api.getPlanHistory();
      setPlanHistory(historyData.plans);
    } catch (err: any) {
      console.log('Could not load plan history');
    }
  };

  const checkAutoPrompt = () => {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0=Sun, 6=Sat
    // Only prompt on Saturday or Sunday when next week not yet generated
    if ((dayOfWeek === 0 || dayOfWeek === 6) && plan) {
      const [py, pm, pd] = plan.week_start_date.split('-').map(Number);
      const planStart = new Date(py, pm - 1, pd);
      const nextMonday = getNextMonday();
      if (planStart < nextMonday) {
        const nextMondayStr = `${nextMonday.getFullYear()}-${String(nextMonday.getMonth()+1).padStart(2,'0')}-${String(nextMonday.getDate()).padStart(2,'0')}`;
        const alreadyGenerated = planHistory.some(h => h.week_start_date >= nextMondayStr);
        setShowPrompt(!alreadyGenerated); // always set — clears if already generated
        return;
      }
    }
    setShowPrompt(false); // reset if not Sat/Sun or plan is current
  };

  const toggleDay = (day: string) => {
    setExpandedDays(prev => {
      const next = new Set(prev);
      if (next.has(day)) next.delete(day);
      else next.add(day);
      return next;
    });
  };

  const getNextMonday = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const daysUntilMonday = dayOfWeek === 0 ? 1 : 8 - dayOfWeek; // If Sunday, 1 day; else 8 - dayOfWeek
    const nextMonday = new Date(today);
    nextMonday.setDate(today.getDate() + daysUntilMonday);
    nextMonday.setHours(0, 0, 0, 0);
    return nextMonday;
  };

  const handleGeneratePlan = async (isRegeneration: boolean = false, overrides?: { run_days: string[]; strength_days: string[]; cycling_days?: string[]; walking_days?: string[]; rest_days: string[] }, explicitWeekStart?: string, runnerNote?: string) => {
    // First-time generation (no existing plan): skip the modal, go straight to generate
    const isFirstTime = !plan && planHistory.length === 0;

    // Show availability modal for week 2+ (both generate-next and regenerate)
    if (!isFirstTime && !overrides) {
      setPendingRegenerate(isRegeneration);
      setShowAvailModal(true);
      return;
    }

    try {
      setGenerating(true);
      setError(null);
      setStatusMessage(null);

      // Anchor to the displayed week's Monday when regenerating or generating from empty state.
      // explicitWeekStart (from the availability modal) wins so a week-level
      // constraint and the generated week always target the same Monday.
      const weekStart = explicitWeekStart ?? (isRegeneration ? plan?.week_start_date : (selectedWeekStart || undefined));
      const result = await api.generatePlan(isRegeneration, overrides, weekStart, runnerNote);

      // Hide the auto-prompt banner
      setShowPrompt(false);

      // Extract the week the plan was generated for (handles ISO "2026-05-04"
      // and RFC-2822 "Mon, 04 May 2026 00:00:00 GMT" depending on Flask version)
      const isoMatch = (result.plan.week_start_date || '').match(/(\d{4})-(\d{2})-(\d{2})/);
      const generatedWeekStart = isoMatch ? `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}` : null;
      const planDate = isoMatch ? new Date(+isoMatch[1], +isoMatch[2] - 1, +isoMatch[3]) : null;

      // Always navigate the user to the week the plan was generated for. The
      // success banner says "Showing week of <X>" — the page must actually
      // show that week. Falling back to "current week" caused a UX dead end
      // when the user's new training cycle started next Monday and the
      // current week had no plan, so no nav was available either.
      if (isRegeneration && weekStart) {
        setSelectedWeekStart(weekStart);
        await loadPlanAndFeasibility(weekStart);
      } else if (generatedWeekStart) {
        setSelectedWeekStart(generatedWeekStart);
        await loadPlanAndFeasibility(generatedWeekStart);
      } else {
        setSelectedWeekStart(null);
        await loadPlanAndFeasibility(null);
      }
      await loadPlanHistory();

      // Show success message
      let successMsg = `✓ Plan ${isRegeneration ? 'regenerated' : 'generated'} successfully!`;
      if (planDate) successMsg += ` Showing week of ${planDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}.`;

      // If max weekly mileage was auto-set, show additional info
      if (result.max_mileage_set && result.recommended_max_mileage) {
        successMsg += `\n\nℹ️ To achieve your race goal, you should be able to handle up to ${result.recommended_max_mileage} miles/week at peak training. This has been set in your race goal. You can adjust it if needed.`;
      }

      // If this was a regeneration due to race goal update
      if (isRegeneration) {
        successMsg += `\n\n✓ Plan adapted to your updated race goal settings.`;
      }

      setStatusMessage(successMsg);
    } catch (err: any) {
      // Backend returns all_complete flag when every workout is done
      if (err.message?.includes('already completed')) {
        setStatusMessage('All workouts this week are complete. Nothing to regenerate.');
      } else {
        setError(err.message);
      }
    } finally {
      setGenerating(false);
    }
  };

  const getStatusIcon = (status: string, workoutType?: string): string => {
    // Rest days should show rest icon regardless of status
    if (workoutType === 'Rest' && (status === 'pending' || status === 'missed')) {
      return '🛌';
    }
    switch (status) {
      case 'completed':
        return '✅';
      case 'partial':
        return '⚠️';
      case 'missed':
        return '❌';
      default:
        return '⏳';
    }
  };

  const getStatusLabel = (status: string, workoutType?: string): string => {
    // Rest days should show "Rest Day" instead of "Pending" or "Missed"
    if (workoutType === 'Rest' && (status === 'pending' || status === 'missed')) {
      return 'Rest Day';
    }
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'partial':
        return 'Partial';
      case 'missed':
        return 'Missed';
      default:
        return 'Pending';
    }
  };

  const { formatPace, formatDist } = useUnits();

  const getPlannedWorkoutForDay = (day: string): PlannedWorkout | undefined => {
    return plannedWorkouts.find(pw => pw.day === day);
  };

  const handleMoveWorkout = async (workoutId: number, newDate: string) => {
    try {
      setMoveError(null);
      await api.movePlannedWorkout(workoutId, newDate);
      setMovingWorkoutId(null);

      // Reload plan data
      await loadPlanAndFeasibility(selectedWeekStart);
    } catch (err: any) {
      setMoveError(err.message);
    }
  };

  const handleDeleteWorkout = async (workoutId: number) => {
    if (!window.confirm('Are you sure you want to delete this planned workout?')) {
      return;
    }

    try {
      await api.deletePlannedWorkout(workoutId);

      // Reload plan data
      await loadPlanAndFeasibility(selectedWeekStart);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const getWorkoutDate = (day: string): string => {
    // First try to get the actual date from planned workouts (most reliable)
    const existingWorkout = plannedWorkouts.find(pw => pw.day === day);
    if (existingWorkout) {
      return existingWorkout.planned_date;
    }

    // Fallback: calculate from plan week_start_date (parse as local to avoid UTC offset issues)
    if (!plan) return '';
    const [y, m, d] = plan.week_start_date.split('-').map(Number);
    const dayIndex = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].indexOf(day);
    const workoutDate = new Date(y, m - 1, d + dayIndex);
    return `${workoutDate.getFullYear()}-${String(workoutDate.getMonth()+1).padStart(2,'0')}-${String(workoutDate.getDate()).padStart(2,'0')}`;
  };

  const loadPlanStatus = async () => {
    try {
      const status = await api.getPlanStatus();
      setPlanStatus(status.plan_status || 'active');
    } catch (err: any) {
      console.log('Could not load plan status:', err);
      setPlanStatus('active');
    }
  };

  const loadProfileDays = async () => {
    try {
      const profile = await api.getRaceGoal();
      setRaceGoal(profile);
      setDefaultRunDays(profile.preferred_run_days || []);
      setDefaultStrengthDays(profile.preferred_strength_days || []);
      setDefaultCyclingDays(profile.preferred_cycling_days || []);
      setDefaultWalkingDays(profile.preferred_walking_days || []);
      setDefaultRestDays(profile.preferred_rest_days || []);
    } catch (err: any) {
      console.log('Could not load profile days:', err);
    }
  };

  const handlePausePlan = async () => {
    if (!window.confirm('Pause your training plan? You can resume it later.')) {
      return;
    }

    try {
      const result = await api.pausePlan();
      setPlanStatus('paused');
      setStatusMessage(result.message);
      setShowPlanMenu(false);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleResumePlan = async () => {
    try {
      const result = await api.resumePlan();
      setPlanStatus('active');
      setStatusMessage(result.message);
      setShowPlanMenu(false);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleCompletePlan = async () => {
    if (!window.confirm('Mark your training plan as completed? This action cannot be undone.')) {
      return;
    }

    try {
      const result = await api.completePlan();
      setPlanStatus('completed');
      setStatusMessage(result.message);
      setShowPlanMenu(false);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleCancelPlan = async () => {
    if (!window.confirm('Cancel your training plan? This action cannot be undone.')) {
      return;
    }

    try {
      const result = await api.cancelPlan();
      setPlanStatus('cancelled');
      setStatusMessage(result.message);
      setShowPlanMenu(false);
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="loading">Loading training plan...</div>
      </>
    );
  }

  if (generating || plan?.review_pending) {
    return (
      <>
        <Navbar />
        <div className="plan-building">
          <div className="spinner" />
          <p>Building your week and having the coach review it…</p>
          <p className="plan-building-sub">This takes a couple of minutes.</p>
        </div>
      </>
    );
  }

  const isRaceCompleted = !selectedWeekStart && !isEmptyWeek
    && raceGoal?.race_date
    && new Date(raceGoal.race_date + 'T00:00:00') < new Date();

  return (
    <>
      <Navbar />
      {showAvailModal && (() => {
        // Derive completed day names from DB-backed planned workouts
        const completedDayNames = plannedWorkouts
          .filter(w => w.status === 'completed')
          .map(w => w.day)
          .filter(Boolean);
        return (
        <AvailabilityModal
          isRegeneration={pendingRegenerate}
          weekStartDate={plan?.week_start_date}
          completedDays={pendingRegenerate ? completedDayNames : []}
          defaultRunDays={defaultRunDays}
          defaultStrengthDays={defaultStrengthDays}
          defaultCyclingDays={defaultCyclingDays}
          defaultWalkingDays={defaultWalkingDays}
          defaultRestDays={defaultRestDays}
          defaultConstraintReason={pendingRegenerate ? ((plan?.constraint_reason as any) || null) : null}
          defaultNote={pendingRegenerate ? (plan?.runner_note || '') : ''}
          onConfirm={async ({ runDays, strengthDays, cyclingDays, walkingDays, restDays }, constraint) => {
            setShowAvailModal(false);

            // Resolve the exact Monday this generation targets, so the week-level
            // constraint is keyed to the same week the plan is generated for.
            const fmt = (d: Date) =>
              `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            const targetWeek = pendingRegenerate
              ? (plan?.week_start_date || '').slice(0, 10)
              : (selectedWeekStart || fmt(getNextMonday()));

            const currentReason = pendingRegenerate ? ((plan?.constraint_reason as any) || null) : null;

            // No-op only when nothing changed — days AND the week's constraint.
            if (pendingRegenerate) {
              const sortedEq = (a: string[], b: string[]) =>
                [...a].sort().join(',') === [...b].sort().join(',');
              if (sortedEq(runDays, defaultRunDays) &&
                  sortedEq(strengthDays, defaultStrengthDays) &&
                  sortedEq(cyclingDays, defaultCyclingDays) &&
                  sortedEq(walkingDays, defaultWalkingDays) &&
                  sortedEq(restDays, defaultRestDays) &&
                  constraint.reason === currentReason &&
                  constraint.note === (plan?.runner_note || '')) {
                return;
              }
            }

            // Persist (or clear) the week-level constraint before generating.
            try {
              if (constraint.reason) {
                await api.setWeekOverride({
                  week_start_date: targetWeek,
                  reason: constraint.reason,
                  max_mileage: constraint.maxMileage,
                });
              } else if (currentReason) {
                await api.deleteWeekOverride(targetWeek).catch(() => {});
              }
            } catch (e) {
              // Non-fatal — fall through and still generate.
            }

            handleGeneratePlan(pendingRegenerate, { run_days: runDays, strength_days: strengthDays, cycling_days: cyclingDays, walking_days: walkingDays, rest_days: restDays }, targetWeek, constraint.note);
          }}
          onCancel={() => setShowAvailModal(false)}
        />
        );
      })()}
      <div className="weekly-plan-page">
        <div className="weekly-plan-container">
          {/* Mileage Plan Overview — always visible above tabs */}
          {feasibility && plan && (() => {
            const rawPeakMileage = feasibility.target_peak_mileage || 0;
            const maxWeeklyMileage = feasibility.max_weekly_mileage || Infinity;
            const peakMileage = Math.min(rawPeakMileage, maxWeeklyMileage);
            const currentWeekNum = plan.week_number || 1;
            const weeksUntilRace = plan.weeks_until_race || 0;
            // weeksUntilRace includes the current week in its count, so subtract 1
            // to avoid a phantom week beyond race day
            const totalWeeks = currentWeekNum + Math.max(weeksUntilRace - 1, 0);

            // Each bar's Monday = current week's Monday offset by (weekNum - current)
            // weeks. Parse as local (not UTC) to avoid an off-by-one date.
            const [cwY, cwM, cwD] = (plan.week_start_date || '').split('-').map(Number);
            const currentMonday = cwY ? new Date(cwY, cwM - 1, cwD) : null;
            const weekStartLabel = (weekNum: number): string => {
              if (!currentMonday) return `W${weekNum}`;
              const dt = new Date(currentMonday);
              dt.setDate(dt.getDate() + (weekNum - currentWeekNum) * 7);
              return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            };

            const phaseFromWutr = (wutr: number): string => {
              if (wutr >= 12) return 'Base';
              if (wutr >= 8) return 'Build';
              if (wutr >= 3) return 'Peak';
              return 'Taper';
            };

            const mileageFromPhase = (phase: string): number => {
              if (phase === 'Base') return peakMileage * 0.65;
              if (phase === 'Build') return peakMileage * 0.80;
              if (phase === 'Peak') return peakMileage * 0.95;
              return peakMileage * 0.60;
            };

            const historyByWeek: Record<number, { mileage: number; completed: number; phase: string }> = {};
            planHistory.forEach((h: any) => {
              historyByWeek[h.week_number] = {
                mileage: h.target_mileage,
                completed: h.completed_mileage || 0,
                phase: h.training_phase,
              };
            });

            const weeks = Array.from({ length: totalWeeks }, (_, i) => {
              const weekNum = i + 1;
              const wutrAtWeek = weeksUntilRace - (weekNum - currentWeekNum);
              const dynPhase = phaseFromWutr(wutrAtWeek);
              const isGenerated = weekNum in historyByWeek;
              const phase = isGenerated ? historyByWeek[weekNum].phase : dynPhase;
              const mileage = isGenerated ? historyByWeek[weekNum].mileage : mileageFromPhase(dynPhase);
              const completed = isGenerated ? historyByWeek[weekNum].completed : 0;
              return { weekNum, mileage, completed, phase, isGenerated };
            });

            // Too many weeks overflow the card on a narrow screen. Show a window
            // around the current week instead: a handful of recent past weeks +
            // current + a couple upcoming. Narrow viewports get fewer bars; wide
            // ones get more. The subtitle/peakLabel still describe the FULL plan.
            const maxBars = viewportWidth < 640 ? 6 : viewportWidth < 1024 ? 9 : 12;
            let displayWeeks = weeks;
            if (weeks.length > maxBars) {
              const futureWanted = Math.min(2, maxBars - 1);
              let endIdx = Math.min(weeks.length, (currentWeekNum - 1) + 1 + futureWanted);
              let startIdx = Math.max(0, endIdx - maxBars);
              endIdx = Math.min(weeks.length, startIdx + maxBars); // refill if clamped at start
              displayWeeks = weeks.slice(startIdx, endIdx);
            }

            // Chart scale must accommodate over-execution too — if the user ran
            // more than the target in a past week, the actual fill should still fit.
            // Scale to the visible window so the shown bars use the full height.
            const maxMileage = Math.max(
              ...displayWeeks.map(w => Math.max(w.mileage, w.completed)),
              1
            );

            const peakWeeks = weeks.filter(w => w.phase === 'Peak');
            const peakLabel = peakWeeks.length > 0
              ? `weeks ${peakWeeks[0].weekNum}–${peakWeeks[peakWeeks.length - 1].weekNum}`
              : null;

            const phaseColor: Record<string, string> = {
              Base: '#4CAF50',
              Build: '#2196F3',
              Peak: '#f97316',
              Taper: '#9C27B0',
            };

            return (
              <div className="mileage-overview">
                <h3>Mileage Plan Overview</h3>
                <p className="mileage-overview-subtitle">
                  {peakLabel
                    ? `Peak training is ${peakLabel} (~${formatDist(peakMileage * 0.95)}/week), followed by a taper into race day.`
                    : `${totalWeeks}-week plan · ${weeksUntilRace} weeks to race`}
                </p>
                <div className="mileage-chart">
                  {displayWeeks.map(({ weekNum, mileage, completed, phase, isGenerated }) => {
                    const isPast = weekNum < currentWeekNum;
                    const isCurrent = weekNum === currentWeekNum;
                    const targetHeightPct = (mileage / maxMileage) * 100;
                    const actualHeightPct = (completed / maxMileage) * 100;

                    // Past + generated: layered (target outline + actual fill).
                    // Current/future: solid bar at target height (current behavior).
                    const showLayered = isPast && isGenerated;

                    let barColor = isGenerated ? phaseColor[phase] : '#c7d2fe';
                    if (isCurrent) barColor = '#6366f1';

                    const tooltip = showLayered
                      ? `Week ${weekNum} (${phase}): ${formatDist(completed, 1)} done of ${formatDist(mileage, 0)} target`
                      : `Week ${weekNum} (${phase}): ~${formatDist(mileage, 0)}${isGenerated ? '' : ' est.'}`;

                    return (
                      <div key={weekNum} className={`mileage-bar-col ${isCurrent ? 'current' : ''}`}>
                        <div className="mileage-bar-wrap" title={tooltip}>
                          {showLayered ? (
                            <>
                              <div
                                className="mileage-bar-target-outline"
                                style={{ height: `${targetHeightPct}%`, borderColor: phaseColor[phase] }}
                              />
                              <div
                                className="mileage-bar-actual"
                                style={{ height: `${actualHeightPct}%` }}
                              />
                            </>
                          ) : (
                            <div
                              className="mileage-bar"
                              style={{
                                height: `${targetHeightPct}%`,
                                backgroundColor: barColor,
                                opacity: isGenerated || isCurrent ? 1 : 0.5,
                              }}
                            />
                          )}
                        </div>
                        <div className="mileage-bar-label">
                          {showLayered
                            ? `${formatDist(completed, 0)}/${formatDist(mileage, 0)}`
                            : formatDist(mileage, 0)}
                        </div>
                        <div className={`mileage-bar-week ${isCurrent ? 'current-label' : ''}`}
                             title={`Week ${weekNum}`}>
                          {weekStartLabel(weekNum)}{isCurrent ? ' ◀' : ''}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mileage-legend">
                  <span><span className="legend-dot" style={{ background: '#22c55e' }} />Completed (actual)</span>
                  <span><span className="legend-dot" style={{ background: 'transparent', border: '1.5px dashed currentColor', opacity: 0.6 }} />Target</span>
                  <span><span className="legend-dot" style={{ background: '#6366f1' }} />Current week</span>
                  <span><span className="legend-dot" style={{ background: '#4CAF50' }} />Base</span>
                  <span><span className="legend-dot" style={{ background: '#2196F3' }} />Build</span>
                  <span><span className="legend-dot" style={{ background: '#f97316' }} />Peak</span>
                  <span><span className="legend-dot" style={{ background: '#9C27B0' }} />Taper</span>
                  <span><span className="legend-dot" style={{ background: '#c7d2fe', border: '1px dashed #818cf8' }} />Projected</span>
                </div>
              </div>
            );
          })()}

          <div className="plan-tabs">
            <button
              className={`plan-tab ${activeTab === 'schedule' ? 'active' : ''}`}
              onClick={() => setActiveTab('schedule')}
            >
              Schedule
            </button>
            <button
              className={`plan-tab ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              Plan Overview
            </button>
            <button
              className={`plan-tab ${activeTab === 'adherence' ? 'active' : ''}`}
              onClick={() => setActiveTab('adherence')}
            >
              Adherence
            </button>
          </div>

          {activeTab === 'adherence' && <AdherenceDashboard embedded />}

          {/* ── PLAN OVERVIEW TAB ─────────────────────────────── */}
          {activeTab === 'overview' && <>
            {/* Training Phase Timeline — top of tab */}
            {plan && (
              <div className="phase-timeline-container">
                <div className="timeline-header">
                  <h3>Training Plan Overview</h3>
                  <span className="timeline-subtitle">{plan.weeks_until_race} weeks until race</span>
                </div>
                <div className="phase-timeline">
                  {(() => {
                    const currentWeek = plan.week_number || 1;
                    const weeksUntilRace = plan.weeks_until_race || 0;
                    const totalWeeks = currentWeek + Math.max(weeksUntilRace - 1, 0);
                    const phaseFromWutr = (wutr: number) => {
                      if (wutr >= 12) return 'Base';
                      if (wutr >= 8) return 'Build';
                      if (wutr >= 3) return 'Peak';
                      return 'Taper';
                    };
                    const phaseColor: Record<string, string> = {
                      Base: '#4CAF50', Build: '#2196F3', Peak: '#FF9800', Taper: '#9C27B0'
                    };
                    const weekPhases = Array.from({ length: totalWeeks }, (_, i) => {
                      const weekNum = i + 1;
                      const wutr = weeksUntilRace - (weekNum - currentWeek);
                      return { weekNum, phase: phaseFromWutr(wutr) };
                    });
                    const segments: { phase: string; start: number; end: number }[] = [];
                    weekPhases.forEach(({ weekNum, phase }) => {
                      const last = segments[segments.length - 1];
                      if (last && last.phase === phase) { last.end = weekNum; }
                      else { segments.push({ phase, start: weekNum, end: weekNum }); }
                    });

                    // Per-phase mileage: use generated weekly targets where we have
                    // them, else estimate from the plan's peak mileage by phase
                    // (mirrors the Mileage Plan Overview chart's phase factors).
                    const rawPeak = feasibility?.target_peak_mileage || 0;
                    const maxWeekly = feasibility?.max_weekly_mileage || Infinity;
                    const peakMileage = Math.min(rawPeak, maxWeekly);
                    const mileageFromPhase = (p: string) =>
                      p === 'Base' ? peakMileage * 0.65 :
                      p === 'Build' ? peakMileage * 0.80 :
                      p === 'Peak' ? peakMileage * 0.95 : peakMileage * 0.60;
                    const historyByWeek: Record<number, number> = {};
                    planHistory.forEach((h: any) => { historyByWeek[h.week_number] = h.target_mileage; });
                    const weekMileage = (weekNum: number, p: string) =>
                      weekNum in historyByWeek ? (historyByWeek[weekNum] || 0) : mileageFromPhase(p);

                    // Date of a week's Monday (+offsetDays), parsed local to avoid
                    // a UTC off-by-one.
                    const [cwY, cwM, cwD] = (plan.week_start_date || '').split('-').map(Number);
                    const currentMonday = cwY ? new Date(cwY, cwM - 1, cwD) : null;
                    const dateFor = (weekNum: number, offsetDays = 0): string => {
                      if (!currentMonday) return '';
                      const dt = new Date(currentMonday);
                      dt.setDate(dt.getDate() + (weekNum - currentWeek) * 7 + offsetDays);
                      return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    };

                    return segments.map((seg, idx) => {
                      const segWeeks = seg.end - seg.start + 1;
                      let phaseTotalMiles = 0;
                      for (let wk = seg.start; wk <= seg.end; wk++) phaseTotalMiles += weekMileage(wk, seg.phase);
                      const phasePerWeek = segWeeks > 0 ? phaseTotalMiles / segWeeks : 0;
                      const dateRange = `${dateFor(seg.start)} – ${dateFor(seg.end, 6)}`;
                      const isCurrentPhase = currentWeek >= seg.start && currentWeek <= seg.end;
                      const isPastPhase = currentWeek > seg.end;
                      const progress = isCurrentPhase
                        ? ((currentWeek - seg.start + 1) / segWeeks) * 100
                        : isPastPhase ? 100 : 0;
                      const color = phaseColor[seg.phase];
                      return (
                        <div
                          key={idx}
                          className={`phase-segment ${isCurrentPhase ? 'active' : ''} ${isPastPhase ? 'completed' : ''}`}
                          style={{ flex: segWeeks }}
                        >
                          <div className="phase-bar" style={{ backgroundColor: color }}>
                            <div className="phase-progress" style={{ width: `${progress}%`, backgroundColor: color, opacity: 1, filter: 'brightness(1.2)' }} />
                            {isCurrentPhase && (
                              <div className="current-marker" style={{ left: `${progress}%` }}>
                                <div className="marker-dot" style={{ backgroundColor: color }} />
                                <div className="marker-label">Week {currentWeek}</div>
                              </div>
                            )}
                          </div>
                          <div className="phase-label">
                            <div className="phase-name">{seg.phase === 'Base' ? 'Base Building' : seg.phase === 'Build' ? 'Build Phase' : seg.phase === 'Peak' ? 'Peak Phase' : 'Taper'}</div>
                            {currentMonday && <div className="phase-weeks">{dateRange}</div>}
                            {phaseTotalMiles > 0 && (
                              <div className="phase-weeks" title={`~${formatDist(phasePerWeek, 0)}/week over ${segWeeks} week${segWeeks > 1 ? 's' : ''}`}>
                                ~{formatDist(phaseTotalMiles, 0)} total · {formatDist(phasePerWeek, 0)}/wk
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            )}

            {/* Goal Feasibility Card */}
            {feasibility && (
              <div className={`feasibility-card feasibility-${feasibility.feasibility_score >= 60 ? 'good' : feasibility.feasibility_score >= 40 ? 'moderate' : 'challenging'}`}>
                <div className="feasibility-header">
                  <h2>Goal Assessment</h2>
                  <div
                    className="feasibility-score"
                    title={feasibility.score_breakdown ?
                      `Score Breakdown:\n` +
                      `• Pace: ${feasibility.score_breakdown.pace_score}/100 (weight: ${Math.round(feasibility.score_breakdown.pace_weight * 100)}%)\n` +
                      `• Mileage: ${feasibility.score_breakdown.mileage_score}/100 (weight: ${Math.round(feasibility.score_breakdown.mileage_weight * 100)}%)\n` +
                      `• Time Until Race: ${feasibility.score_breakdown.time_score}/100 (weight: ${Math.round(feasibility.score_breakdown.time_weight * 100)}%)\n` +
                      (feasibility.score_breakdown.adherence_score !== null && feasibility.score_breakdown.adherence_score !== undefined ?
                        `• Adherence: ${feasibility.score_breakdown.adherence_score}/100 (weight: ${Math.round(feasibility.score_breakdown.adherence_weight! * 100)}%)\n` : '') +
                      `\nHover for details on each component`
                      : 'Calculating feasibility based on pace, mileage, time, and adherence'}
                    style={{cursor: 'help'}}
                  >
                    {feasibility.feasibility_score}/100
                  </div>
                </div>
                <div className="feasibility-assessment">{feasibility.assessment}</div>

                {(feasibility.current_pace || feasibility.current_pace_formatted) &&
                 (feasibility.goal_pace || feasibility.goal_pace_formatted) && (
                  <div className="pace-comparison">
                    <div className="pace-item">
                      <span className="pace-label">
                        Current Pace
                        <span className="pace-info-icon" aria-label="How is this calculated?">
                          ⓘ
                          <span className="pace-tooltip">
                            Estimated from your last 4 weeks of runs. If you have tempo workouts, we use the median tempo pace × 0.90. Otherwise we use the median easy run pace × 0.80. This approximates the race pace you could sustain today.
                          </span>
                        </span>
                      </span>
                      <span className="pace-value">
                        {feasibility.current_pace
                          ? formatPace(feasibility.current_pace)
                          : feasibility.current_pace_formatted}
                      </span>
                    </div>
                    <div className="pace-item">
                      <span className="pace-label">
                        Goal Pace
                        <span className="pace-info-icon" aria-label="How is this calculated?">
                          ⓘ
                          <span className="pace-tooltip">
                            Derived from your goal finish time divided by your race distance.
                          </span>
                        </span>
                      </span>
                      <span className="pace-value">
                        {feasibility.goal_pace
                          ? formatPace(feasibility.goal_pace)
                          : feasibility.goal_pace_formatted}
                      </span>
                    </div>
                  </div>
                )}

              </div>
            )}


            {feasibility && plan && (
              <div className="recommendations">
                <h3>Recommendations:</h3>
                <ul>
                  {feasibility.recommendations.map((rec, idx) => (
                    <li key={idx}>{rec}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Plan History Section */}
            {planHistory.length > 0 && (
              <div className="plan-history-section">
                <div className="history-header">
                  <h2>📅 Training History</h2>
                  <button
                    onClick={() => setShowHistory(!showHistory)}
                    className="btn-toggle-history"
                  >
                    {showHistory ? '▼ Hide Past Weeks' : '▶ View Past Weeks'}
                  </button>
                </div>

                {showHistory && (
                  <div className="history-list">
                    {planHistory.filter((historicalPlan) => {
                      const weekStart = new Date(historicalPlan.week_start_date);
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      return weekStart <= today;
                    }).map((historicalPlan) => {
                      const weekStart = new Date(historicalPlan.week_start_date);
                      const weekEnd = new Date(historicalPlan.week_end_date);
                      const isCurrentWeek = plan && historicalPlan.week_start_date === plan.week_start_date;

                      return (
                        <div
                          key={historicalPlan.id}
                          className={`history-week-card ${isCurrentWeek ? 'current-week' : ''}`}
                        >
                          <div className="history-week-header">
                            <div className="week-info">
                              <h3>
                                Week {historicalPlan.week_number} - {historicalPlan.training_phase}
                                {isCurrentWeek && <span className="current-badge">Current</span>}
                              </h3>
                              <p className="week-dates">
                                {weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </p>
                            </div>
                            <div className="week-adherence">
                              <div className="adherence-score" style={{
                                color: historicalPlan.adherence_score >= 80 ? '#28a745' :
                                       historicalPlan.adherence_score >= 60 ? '#ffc107' : '#ff9800'
                              }}>
                                {historicalPlan.adherence_score}%
                              </div>
                              <div className="adherence-label">Adherence</div>
                            </div>
                          </div>

                          <div className="history-week-stats">
                            <div className="stat-item">
                              <span className="stat-label">Mileage:</span>
                              <span className="stat-value">
                                {historicalPlan.completed_mileage} / {historicalPlan.target_mileage} mi
                              </span>
                            </div>
                            {historicalPlan.target_elevation > 0 && (
                              <div className="stat-item">
                                <span className="stat-label">Elevation:</span>
                                <span className="stat-value">
                                  {historicalPlan.completed_elevation} / {historicalPlan.target_elevation} ft
                                </span>
                              </div>
                            )}
                            <div className="stat-item">
                              <span className="stat-label">Workouts:</span>
                              <span className="stat-value">
                                {historicalPlan.workouts_completed} / {historicalPlan.workouts_planned} completed
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>}

          {/* ── SCHEDULE TAB ──────────────────────────────────── */}
          {activeTab === 'schedule' && <>
            {/* Status Message */}
            {statusMessage && (
              <div className="status-message success">
                {statusMessage}
                <button onClick={() => setStatusMessage(null)} className="btn-dismiss-status">✕</button>
              </div>
            )}

            {/* Plan Status Banners */}
            {plan && planStatus === 'paused' && (
              <div className="plan-status-banner paused">
                <span className="status-icon">⏸️</span>
                <div>
                  <strong>Plan Paused</strong>
                  <p>Your training plan is paused. You can still view completed workouts but won't generate new weeks.</p>
                </div>
              </div>
            )}
            {plan && planStatus === 'completed' && (
              <div className="plan-status-banner completed">
                <span className="status-icon">🎉</span>
                <div>
                  <strong>Plan Completed!</strong>
                  <p>Congratulations on completing your training plan! Ready to set a new goal?</p>
                </div>
              </div>
            )}
            {plan && planStatus === 'cancelled' && (
              <div className="plan-status-banner cancelled">
                <span className="status-icon">❌</span>
                <div>
                  <strong>Plan Cancelled</strong>
                  <p>This training plan has been cancelled. You can set a new race goal to start fresh.</p>
                </div>
              </div>
            )}

            {/* Auto-prompt — slim strip */}
            {showPrompt && plan && (
              <div className="auto-prompt-strip">
                <span className="prompt-strip-dot" />
                <span className="prompt-strip-text">Next week's plan not generated yet</span>
                <button
                  onClick={() => handleGeneratePlan(false)}
                  className="btn-prompt-strip"
                  disabled={generating}
                >
                  {generating ? 'Generating…' : '✨ Generate'}
                </button>
                <button onClick={() => setShowPrompt(false)} className="btn-dismiss-strip">✕</button>
              </div>
            )}

            {error && <div ref={errorRef} className="error-message">{error}</div>}

            {/* Empty week — user navigated to a week with no plan */}
            {isEmptyWeek && selectedWeekStart && (
              <div className="empty-week-container">
                <div className="empty-week-nav">
                  <button
                    className="btn-week-nav"
                    onClick={() => {
                      const [y, m, d] = selectedWeekStart.split('-').map(Number);
                      const prev = new Date(y, m - 1, d - 7);
                      setSelectedWeekStart(`${prev.getFullYear()}-${String(prev.getMonth()+1).padStart(2,'0')}-${String(prev.getDate()).padStart(2,'0')}`);
                    }}
                  >◀ Prev</button>
                  <button
                    className="btn-week-nav btn-current-week"
                    onClick={() => setSelectedWeekStart(null)}
                  >📅 Today</button>
                  <button
                    className="btn-week-nav"
                    onClick={() => {
                      const [y, m, d] = selectedWeekStart.split('-').map(Number);
                      const next = new Date(y, m - 1, d + 7);
                      setSelectedWeekStart(`${next.getFullYear()}-${String(next.getMonth()+1).padStart(2,'0')}-${String(next.getDate()).padStart(2,'0')}`);
                    }}
                  >Next ▶</button>
                </div>
                <div className="empty-week-card">
                  <div className="empty-week-icon">📭</div>
                  <h3>No plan for this week</h3>
                  <p className="empty-week-date">
                    {(() => {
                      const [y, m, d] = selectedWeekStart.split('-').map(Number);
                      const start = new Date(y, m - 1, d);
                      const end = new Date(y, m - 1, d + 6);
                      return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
                    })()}
                  </p>
                  <p className="empty-week-message">No training plan was generated for this week.</p>
                  <button
                    className="btn-generate-plan"
                    onClick={() => handleGeneratePlan(false)}
                    disabled={generating}
                  >
                    {generating ? 'Generating...' : 'Generate Plan'}
                  </button>
                  {/* Show any actual workouts logged this week even without a plan */}
                  {(Object.keys(extraWorkouts).length > 0 || crossTraining.length > 0) && (
                    <div className="empty-week-runs">
                      <h4>Workouts logged this week:</h4>
                      {crossTraining.map((ct, i) => (
                        <div key={`ct-${i}`} className="empty-week-run-day">
                          <span className="empty-week-run-date">
                            {ct.workout_type === 'Ride' ? '🚴' : '🚶'} {(ct.day || '').slice(0, 3)}
                          </span>
                          <span className="empty-week-run-detail">
                            {ct.workout_type} · {ct.distance ? formatDist(ct.distance) : `${Math.round((ct.duration_seconds || 0) / 60)} min`}
                          </span>
                        </div>
                      ))}
                      {Object.entries(extraWorkouts).sort(([a], [b]) => a.localeCompare(b)).map(([date, runs]) => (
                        <div key={date} className="empty-week-run-day">
                          <span className="empty-week-run-date">
                            {(() => {
                              const [ey, em, ed] = date.split('-').map(Number);
                              return new Date(ey, em - 1, ed).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                            })()}
                          </span>
                          {(runs as any[]).map((run, i) => (
                            <span key={i} className="empty-week-run-detail">
                              {run.workout_type} · {formatDist(run.distance)}
                            </span>
                          ))}
                        </div>
                      ))}
                      <p className="empty-week-total">
                        Total: {formatDist(Object.values(extraWorkouts).flat().reduce((sum: number, r: any) => sum + (r.distance || 0), 0))}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Race Completed — race date has passed, user is on default view (not navigating history) */}
            {isRaceCompleted && (
              <div className="race-completed">
                <div className="race-completed__icon">🎉</div>
                <h2>Race Complete!</h2>
                <p className="race-completed__summary">
                  You finished your training for <strong>{raceGoal.race_name}</strong>
                  {raceGoal.race_date && (
                    <> on {new Date(raceGoal.race_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</>
                  )}.
                  {' '}Congratulations!
                </p>
                {raceGoal.next_race_name ? (
                  <>
                    <p className="race-completed__cta">
                      Up next: <strong>{raceGoal.next_race_name}</strong>
                      {raceGoal.next_race_date && (
                        <> on {new Date(raceGoal.next_race_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</>
                      )}
                      {raceGoal.next_race_distance_miles != null && <> · {formatDist(raceGoal.next_race_distance_miles)}</>}
                      {raceGoal.next_race_goal_finish_time_seconds ? (
                        <> · Goal {new Date(raceGoal.next_race_goal_finish_time_seconds * 1000).toISOString().substring(11, 19)}</>
                      ) : null}
                      <br />
                      Generate next week's plan and your training rolls into it — starting with a recovery week.
                    </p>
                    <div className="race-completed__actions">
                      <button
                        className="btn-primary"
                        onClick={() => handleGeneratePlan(false)}
                        disabled={generating}
                      >
                        {generating ? 'Generating…' : "Generate Next Week's Plan"}
                      </button>
                      <button className="race-completed__find-btn" onClick={() => navigate('/race-goal')}>
                        Edit Next Race
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="race-completed__cta">Ready for your next challenge?</p>
                    <div className="race-completed__actions">
                      <button
                        className="btn-primary"
                        onClick={() => navigate('/race-goal')}
                      >
                        Set a New Race Goal
                      </button>
                      <button
                        className="race-completed__find-btn"
                        onClick={() => navigate('/recommend')}
                      >
                        Find a Race Near Me
                      </button>
                    </div>
                  </>
                )}
                <button
                  className="race-completed__history-link"
                  onClick={() => { setShowHistory(true); loadPlanHistory(); }}
                >
                  View past training plans
                </button>
              </div>
            )}

            {/* Plan inactive — most recent TrainingPlan is completed/expired/abandoned */}
            {planInactive && !selectedWeekStart && !isRaceCompleted && (
              <div className="race-completed">
                <div className="race-completed__icon">
                  {planInactive.status === 'completed' ? '🏁' : '⏰'}
                </div>
                <h2>
                  {planInactive.status === 'completed'
                    ? 'Training Plan Complete'
                    : 'Your Training Plan Has Ended'}
                </h2>
                <p className="race-completed__summary">
                  Your last training plan was for <strong>{planInactive.race_name}</strong> on{' '}
                  {new Date(planInactive.race_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}.
                </p>
                {raceGoal?.next_race_name ? (
                  <p className="race-completed__cta">
                    Up next: <strong>{raceGoal.next_race_name}</strong>
                    {raceGoal.next_race_date && (
                      <> on {new Date(raceGoal.next_race_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</>
                    )}
                    . Generate next week's plan to roll into it.
                  </p>
                ) : (
                  <p className="race-completed__cta">Set a new race goal to generate a fresh training plan.</p>
                )}
                <div className="race-completed__actions">
                  <button className="btn-primary" onClick={() => navigate('/race-goal')}>
                    {raceGoal?.next_race_name ? 'Edit Next Race' : 'Set a New Race Goal'}
                  </button>
                  <button className="race-completed__find-btn" onClick={() => navigate('/race-history')}>
                    View Race History
                  </button>
                </div>
              </div>
            )}

            {/* Generate Plan Section — no plan and no completed race to show */}
            {!plan && !planInactive && !isEmptyWeek && !isRaceCompleted && (
              <div className="no-plan">
                <h2>No Training Plan Generated Yet</h2>
                <p>Generate a personalized weekly training plan based on your race goal and current fitness.</p>
                <button
                  onClick={() => handleGeneratePlan(false)}
                  className="btn-primary"
                  disabled={generating}
                >
                  {generating ? 'Generating Plan...' : '🎯 Generate Training Plan'}
                </button>
              </div>
            )}

            {/* Weekly Plan Display — hide when showing race-completed state */}
            {plan && !planInactive && !isRaceCompleted && (
              <>
                {/* Top bar: week nav + plan info + actions */}
                <div className="schedule-top-bar">
                  <div className="schedule-nav">
                    <button
                      onClick={() => {
                        const [y, m, d] = plan.week_start_date.split('-').map(Number);
                        const prev = new Date(y, m - 1, d - 7);
                        setSelectedWeekStart(`${prev.getFullYear()}-${String(prev.getMonth()+1).padStart(2,'0')}-${String(prev.getDate()).padStart(2,'0')}`);
                      }}
                      className="btn-week-nav"
                      title="Previous week"
                    >
                      ◀ Prev
                    </button>
                    <button
                      onClick={() => setSelectedWeekStart(null)}
                      className="btn-week-nav btn-current-week"
                      disabled={!selectedWeekStart}
                    >
                      📅 Today
                    </button>
                    <button
                      onClick={() => {
                        const [y, m, d] = plan.week_start_date.split('-').map(Number);
                        const next = new Date(y, m - 1, d + 7);
                        setSelectedWeekStart(`${next.getFullYear()}-${String(next.getMonth()+1).padStart(2,'0')}-${String(next.getDate()).padStart(2,'0')}`);
                      }}
                      className="btn-week-nav"
                      title="Next week"
                    >
                      Next ▶
                    </button>
                  </div>

                  <div className="schedule-week-meta">
                    <span className={`phase-badge phase-${plan.training_phase.toLowerCase()}`}>{plan.training_phase}</span>
                    {plan.is_constrained && (
                      <span className="constraint-badge" title="Reduced week — easy running only, kept out of your build's progression">
                        🌙 {({ recovery: 'Recovery week', travel: 'Travel week', reduced: 'Reduced week', unavailable: 'Off week' } as Record<string, string>)[plan.constraint_reason || ''] || 'Reduced week'}
                      </span>
                    )}
                    <span className="weeks-badge">Wk{plan.week_number} · {plan.weeks_until_race}wks to race</span>
                    {plan.methodology_display && (
                      <span
                        className="methodology-badge"
                        title={plan.methodology_display.description}
                      >
                        📐 {plan.methodology_display.name}
                      </span>
                    )}
                  </div>

                  <div className="plan-actions">
                    <button onClick={() => navigate('/race-goal')} className="btn-preferences" title="Schedule Preferences">
                      ⚙️
                    </button>
                    {planStatus === 'active' && (
                      <button onClick={() => handleGeneratePlan(true)} className="btn-regenerate" disabled={generating} title="Regenerate Plan">
                        {generating ? 'Regenerating…' : '🔄 Regenerate'}
                      </button>
                    )}
                    <div className="plan-menu">
                      <button
                        onClick={() => setShowPlanMenu(!showPlanMenu)}
                        className="btn-plan-menu"
                      >
                        ⋮
                      </button>
                      {showPlanMenu && (
                        <div className="plan-menu-dropdown">
                          {planStatus === 'active' && (
                            <>
                              <button onClick={handlePausePlan} className="menu-item">⏸️ Pause Plan</button>
                              <button onClick={handleCompletePlan} className="menu-item">✅ Complete Plan</button>
                              <button onClick={handleCancelPlan} className="menu-item menu-item-danger">❌ Cancel Plan</button>
                            </>
                          )}
                          {planStatus === 'paused' && (
                            <>
                              <button onClick={handleResumePlan} className="menu-item">▶️ Resume Plan</button>
                              <button onClick={handleCompletePlan} className="menu-item">✅ Complete Plan</button>
                              <button onClick={handleCancelPlan} className="menu-item menu-item-danger">❌ Cancel Plan</button>
                            </>
                          )}
                          {(planStatus === 'completed' || planStatus === 'cancelled') && (
                            <div className="menu-item menu-item-disabled">Plan is {planStatus}</div>
                          )}
                          <button onClick={() => navigate('/races')} className="menu-item">🔁 Continue into next race</button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Stats strip */}
                <div className="schedule-stats-strip">
                  <div className="sstat">
                    <div className="sstat-label">Target</div>
                    <div className="sstat-value">{formatDist(plan.target_mileage)}</div>
                  </div>
                  <div className="sstat">
                    <div className="sstat-label">Done</div>
                    <div className="sstat-value">{adherence ? formatDist(adherence.mileage_completed) : '—'}</div>
                  </div>
                  <div className="sstat">
                    <div className="sstat-label">Progress</div>
                    <div className="sstat-value">
                      {adherence ? `${Math.round(adherence.mileage_completed / adherence.mileage_planned * 100)}%` : '—'}
                    </div>
                  </div>
                  <div className="sstat">
                    <div className="sstat-label">Adherence</div>
                    <div className="sstat-value" style={adherence ? {
                      color: adherence.adherence_score >= 85 ? '#28a745' :
                             adherence.adherence_score >= 70 ? '#ffc107' :
                             adherence.adherence_score >= 50 ? '#ff9800' : '#dc3545'
                    } : {}}>
                      {adherence ? `${adherence.adherence_score}%` : '—'}
                    </div>
                  </div>
                </div>

                <div className="workouts-schedule">
                  {plan.workouts.map((workout, idx) => {
                    const plannedWorkout = getPlannedWorkoutForDay(workout.day);
                    const hasStatus = plannedWorkout && plannedWorkout.status !== 'pending';
                    const isExpanded = expandedDays.has(workout.day);
                    const isRest = workout.type === 'Rest';
                    const isStrength = workout.type === 'Strength Training';
                    const isCross = workout.type === 'Ride' || workout.type === 'Walk';
                    const crossLabel = workout.type === 'Ride' ? '🚴 Cycling' : '🚶 Walking';

                    const statusKey = isRest && plannedWorkout?.status === 'pending' ? 'rest' : plannedWorkout?.status;
                    const isCompleted = plannedWorkout?.status === 'completed' || plannedWorkout?.status === 'partial';
                    const completedWorkout = plannedWorkout?.completed_workout;

                    // Rest override: runner did a workout on a rest day
                    const isRestOverride = isRest && isCompleted && plannedWorkout?.adherence_score != null;

                    // Target metric — distance if set, pace hint otherwise
                    const targetMetric = isRest ? null
                      : isStrength || isCross ? null
                      : workout.distance > 0 ? formatDist(workout.distance)
                      : workout.pace ? workout.pace.split('\n')[0].slice(0, 30) : null;

                    // Completed metric shown instead when done
                    const doneMetric = isCompleted && completedWorkout
                      ? isStrength || isCross ? '✓ done' : formatDist(completedWorkout.distance)
                      : isRestOverride && completedWorkout
                      ? formatDist(completedWorkout.distance)
                      : null;

                    const summaryMetric = doneMetric ?? targetMetric;

                    return (
                      <div
                        key={idx}
                        className={`workout-day ${isRest ? 'rest-day' : ''} ${hasStatus ? 'has-status' : ''} ${isExpanded ? 'expanded' : ''}`}
                      >
                        {/* ── Summary row (always visible, clickable) ── */}
                        <div
                          className="workout-summary"
                          onClick={() => toggleDay(workout.day)}
                          role="button"
                          aria-expanded={isExpanded}
                        >
                          <div className="summary-left">
                            <span className="summary-day">
                              {workout.day.slice(0, 3)}
                              {(() => {
                                const d = getWorkoutDate(workout.day);
                                if (!d) return null;
                                const [, mm, dd] = d.split('-');
                                return <span className="summary-date">{parseInt(mm)}/{parseInt(dd)}</span>;
                              })()}
                            </span>
                            <span className="summary-type">{isCross ? crossLabel : workout.type}</span>
                            {summaryMetric && (
                              <span className={`summary-metric ${doneMetric ? 'summary-metric--done' : ''}`}>
                                {summaryMetric}
                              </span>
                            )}
                          </div>
                          <div className="summary-right">
                            {plannedWorkout?.adherence_score != null && (
                              <span className="summary-score" style={{
                                color: plannedWorkout.adherence_score >= 80 ? '#28a745' :
                                       plannedWorkout.adherence_score >= 60 ? '#ffc107' :
                                       plannedWorkout.adherence_score > 0 ? '#ff9800' : '#dc3545'
                              }}>
                                {plannedWorkout.adherence_score}/100
                              </span>
                            )}
                            {plannedWorkout && (
                              <span className="status-dot">
                                {getStatusIcon(plannedWorkout.status, workout.type)}
                              </span>
                            )}
                            <span className="expand-chevron">{isExpanded ? '▲' : '▼'}</span>
                          </div>
                        </div>

                        {/* ── Expanded details ── */}
                        {isExpanded && (
                          <div className="workout-details-panel">
                            {plannedWorkout && (
                              <div className="detail-status-row">
                                <span className={`status-indicator status-${statusKey}`}>
                                  {getStatusIcon(plannedWorkout.status, workout.type)} {getStatusLabel(plannedWorkout.status, workout.type)}
                                </span>
                              </div>
                            )}

                            {workout.type === 'Magic Mile' && (
                              <div className="magic-mile-badge">
                                <span className="magic-mile-icon">⚡</span>
                                <span className="magic-mile-text">
                                  Calibration Test — your mile time will update your finish time prediction
                                </span>
                              </div>
                            )}

                            {isRestOverride && plannedWorkout?.completed_workout && (
                              <div className="rest-override-banner">
                                <span className="rest-override-icon">⚠️</span>
                                <span className="rest-override-text">
                                  Rest overridden — {formatDist(plannedWorkout.completed_workout.distance)} ran
                                </span>
                                {plannedWorkout.adherence_score != null && (
                                  <span className="rest-override-score" style={{
                                    color: plannedWorkout.adherence_score >= 70 ? '#ffc107' : '#ff9800'
                                  }}>
                                    {plannedWorkout.adherence_score}/80
                                  </span>
                                )}
                              </div>
                            )}

                            {!isRest && !isStrength && (
                              <>
                                <div className="workout-details">
                                  <span className="workout-distance">{formatDist(workout.distance)}</span>
                                  <span className="workout-pace">{workout.pace}</span>
                                </div>

                                {plannedWorkout && plannedWorkout.completed_workout && (
                                  <div className="completion-comparison">
                                    <div className="comparison-row">
                                      <span className="comparison-label">Completed:</span>
                                      <span className="comparison-value">
                                        {formatDist(plannedWorkout.completed_workout.distance)}
                                        ({Math.round(plannedWorkout.completed_workout.distance / workout.distance * 100)}%)
                                      </span>
                                    </div>
                                    {plannedWorkout.completed_workout.avg_pace && (
                                      <div className="comparison-row">
                                        <span className="comparison-label">Pace:</span>
                                        <span className="comparison-value">
                                          {formatPace(plannedWorkout.completed_workout.avg_pace ?? undefined)}
                                        </span>
                                      </div>
                                    )}
                                    {plannedWorkout.adherence_score !== null && (
                                      <>
                                        <div className="comparison-row">
                                          <span className="comparison-label">Score:</span>
                                          <span className="comparison-value score-value" style={{
                                            color: plannedWorkout.adherence_score >= 80 ? '#28a745' :
                                                   plannedWorkout.adherence_score >= 60 ? '#ffc107' : '#ff9800'
                                          }}>
                                            {plannedWorkout.adherence_score}/100
                                          </span>
                                        </div>
                                        {plannedWorkout.adherence_explanation && (
                                          <div className="adherence-explanation">
                                            {plannedWorkout.adherence_explanation}
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </div>
                                )}

                                {(() => {
                                  const structure: WorkoutStructure | undefined =
                                    plannedWorkout?.workout_structure || (workout as any).structure;
                                  if (structure) {
                                    return (
                                      <div className="workout-structure">
                                        <p className="workout-purpose">{structure.purpose}</p>
                                        <div className="workout-steps">
                                          {structure.steps.map((s, si) => (
                                            <div key={si} className={`workout-step step-${s.type}`}>
                                              <div className="step-header">
                                                <span className="step-type-chip">{s.label}</span>
                                                <div className="step-metrics">
                                                  {s.distance_miles != null && (
                                                    <span className="step-chip">{formatDist(s.distance_miles)}</span>
                                                  )}
                                                  {s.duration_min != null && (
                                                    <span className="step-chip">{s.duration_min} min</span>
                                                  )}
                                                  {s.pace_label && (
                                                    <span className="step-chip step-chip--pace">{s.pace_label}</span>
                                                  )}
                                                  {s.hr_label && (
                                                    <span className="step-chip step-chip--hr">{s.hr_label}</span>
                                                  )}
                                                </div>
                                              </div>
                                              <p className="step-why">{s.why}</p>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    );
                                  }
                                  return (
                                    <div className="workout-notes">
                                      {workout.notes && workout.notes.split('\n').map((line: string, i: number) => (
                                        <div key={i}>{line}</div>
                                      ))}
                                    </div>
                                  );
                                })()}

                                {(() => {
                                  const workoutDate = getWorkoutDate(workout.day);
                                  const extras = extraWorkouts[workoutDate] || [];
                                  if (!extras.length) return null;
                                  return (
                                    <div className="bonus-workouts">
                                      {extras.map((extra: any, i: number) => (
                                        <div key={i} className="bonus-workout">
                                          <span className="bonus-label">+ Bonus run</span>
                                          <span>{formatDist(extra.distance)}</span>
                                          {extra.avg_pace && <span>{formatPace(extra.avg_pace)}</span>}
                                        </div>
                                      ))}
                                    </div>
                                  );
                                })()}
                              </>
                            )}

                            {isStrength && (
                              <>
                                {plannedWorkout && plannedWorkout.completed_workout && (
                                  <div className="completion-status">✓ Strength session completed</div>
                                )}
                                {(() => {
                                  const structure: WorkoutStructure | undefined =
                                    plannedWorkout?.workout_structure || (workout as any).structure;
                                  if (structure) {
                                    return (
                                      <div className="workout-structure">
                                        <p className="workout-purpose">{structure.purpose}</p>
                                        <div className="workout-steps">
                                          {structure.steps.map((s, si) => (
                                            <div key={si} className={`workout-step step-${s.type}`}>
                                              <div className="step-header">
                                                <span className="step-type-chip">{s.label}</span>
                                                <div className="step-metrics">
                                                  {s.duration_min != null && (
                                                    <span className="step-chip">{s.duration_min} min</span>
                                                  )}
                                                  {s.hr_label && (
                                                    <span className="step-chip step-chip--hr">{s.hr_label}</span>
                                                  )}
                                                </div>
                                              </div>
                                              <p className="step-why">{s.why}</p>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    );
                                  }
                                  return (
                                    <div className="workout-notes">
                                      {workout.notes && workout.notes.split('\n').map((line: string, i: number) => (
                                        <div key={i}>{line}</div>
                                      ))}
                                    </div>
                                  );
                                })()}
                              </>
                            )}

                            {isRest && (
                              (() => {
                                const structure: WorkoutStructure | undefined =
                                  plannedWorkout?.workout_structure || (workout as any).structure;
                                if (structure) {
                                  return <p className="workout-purpose rest-purpose">{structure.purpose}</p>;
                                }
                                return workout.notes ? <div className="rest-notes">{workout.notes}</div> : null;
                              })()
                            )}

                            {plannedWorkout && plannedWorkout.status !== 'completed' && (
                              <div className="workout-actions">
                                {moveError && movingWorkoutId === plannedWorkout.id && (
                                  <div className="move-error">{moveError}</div>
                                )}
                                {movingWorkoutId === plannedWorkout.id ? (
                                  <div className="move-selector">
                                    <label>Move to:</label>
                                    <select
                                      onChange={(e) => {
                                        const newDate = e.target.value;
                                        if (newDate) handleMoveWorkout(plannedWorkout.id, newDate);
                                      }}
                                      defaultValue=""
                                    >
                                      <option value="">Select day…</option>
                                      {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => {
                                        const targetDate = getWorkoutDate(day);
                                        const isCurrent = day === workout.day;
                                        return (
                                          <option key={day} value={targetDate} disabled={isCurrent}>
                                            {day} {isCurrent ? '(current)' : ''}
                                          </option>
                                        );
                                      })}
                                    </select>
                                    <button
                                      onClick={() => { setMovingWorkoutId(null); setMoveError(null); }}
                                      className="btn-cancel-move"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                ) : (
                                  <div className="action-buttons">
                                    <button onClick={() => setMovingWorkoutId(plannedWorkout.id)} className="btn-move">
                                      📅 Move
                                    </button>
                                    <button onClick={() => handleDeleteWorkout(plannedWorkout.id)} className="btn-delete">
                                      🗑️ Delete
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {crossTraining.length > 0 && (
                  <div style={{
                    marginTop: 16, padding: '12px 14px', borderRadius: 10,
                    background: 'var(--bg-elevated, #172038)',
                    border: '1px solid rgba(50,162,230,0.10)',
                  }}>
                    <div style={{
                      fontSize: 12, fontWeight: 600, letterSpacing: 0.3, marginBottom: 8,
                      color: 'var(--text-secondary, #7A9BB5)',
                    }}>
                      CROSS-TRAINING <span style={{ fontWeight: 400, opacity: 0.7 }}>· logged, not scored</span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                      {crossTraining.map((ct, i) => (
                        <span key={i} style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          fontSize: 13, color: 'var(--text-secondary, #7A9BB5)',
                          padding: '4px 10px', borderRadius: 8,
                          background: 'rgba(50,162,230,0.06)',
                        }}>
                          <span>{ct.workout_type === 'Ride' ? '🚴' : '🚶'}</span>
                          <span style={{ opacity: 0.75 }}>{(ct.day || '').slice(0, 3)}</span>
                          <span>{ct.distance ? formatDist(ct.distance) : `${Math.round((ct.duration_seconds || 0) / 60)} min`}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </>}
        </div>
      </div>
    </>
  );
};

export default WeeklyPlan;
